import {
  competitionState,
  disciplineForSection,
  gymLocalNow,
  type CompetitionDiscipline,
  type CompetitionScoringType,
  type CompetitionState,
  type Discipline,
} from '@structural-climbing/validation'

// Re-exported so the competitions feature imports its types from one module.
// The scoring type comes from the validation package rather than being spelled
// out again here, so a new formula cannot be half-added.
export { competitionState, gymLocalNow, type CompetitionScoringType, type CompetitionState }

export type CompetitionFormat = 'long_running' | 'single_day'

export interface Competition {
  id: string
  gym_id: string
  name: string
  description: string | null
  prizes: string | null
  /** `combined` has a bouldering and a lead board; see `competitionBoards`. */
  discipline: CompetitionDiscipline
  format: CompetitionFormat
  scoring_type: CompetitionScoringType
  /** Gym-local wall clock, "YYYY-MM-DDTHH:MM:SS". Never a UTC instant. */
  starts_at: string
  ends_at: string
  published: boolean
  join_policy: 'open' | 'members_only'
  requires_confirmation: boolean
  entry_fee_cents: number | null
  entry_fee_currency: string | null
  poster_url: string | null
  created_at: string
  entry_count?: number
  route_count?: number
}

export interface CompetitionCategory {
  id: string
  name: string
  description: string | null
  min_age: number | null
  max_age: number | null
  gender: 'any' | 'male' | 'female'
  capacity: number | null
  sort_order: number
  rounds: string
  entry_count: number
}

export interface CompetitionSession {
  id: string
  competition_id: string
  name: string
  starts_at: string
  ends_at: string
  /** null = uncapped. */
  capacity: number | null
  sort_order: number
}

/**
 * A block of routes with its own window inside a long-running competition.
 * Rounds are to `long_running` what sessions are to `single_day`.
 */
export interface CompetitionRound {
  id: string
  competition_id: string
  name: string
  /** Gym-local wall clock, like the competition's own window. */
  starts_at: string
  ends_at: string
  sort_order: number
}

export interface CompetitionRouteRef {
  id: string
  route_id: string
  label: string | null
  sort_order: number
  /** null = live for the whole competition rather than one round. */
  round_id: string | null
  route: {
    id: string
    /** null for a route set for this competition: those are numbered, not graded. */
    grade: number | null
    color: string | null
    status: 'active' | 'archived'
    section_id: string | null
    setter_note: string | null
    image_r2_key: string | null
    competition_only: boolean
    /** Both optional while a deploy is mid-flight and the Worker predates them. */
    created_at?: string
    set_by?: string | null
    setter?: { name: string | null } | null
  } | null
}

/**
 * A route created for this competition. `competition_only` is still true while
 * it awaits the gym's convert-or-archive decision.
 */
export interface ProvenanceRoute {
  id: string
  /** null until the gym converts it and gives it one. */
  grade: number | null
  color: string | null
  status: 'active' | 'archived'
  competition_only: boolean
  image_r2_key: string | null
  /** Optional while a deploy is mid-flight and the Worker predates it. */
  section_id?: string | null
  /** For the route detail. Optional for the same reason. */
  setter_note?: string | null
  created_at?: string
  set_by?: string | null
  setter?: { name: string | null } | null
}

export interface CompetitionDetail extends Competition {
  sessions: CompetitionSession[]
  rounds: CompetitionRound[]
  categories: CompetitionCategory[]
  routes: CompetitionRouteRef[]
  provenance_routes: ProvenanceRoute[]
}

/** Everything the shared MemberIdentity component needs to render a person. */
export interface CompetitorProfile {
  id: string
  first_name: string | null
  last_name: string | null
  nickname: string | null
  email?: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
}

export interface CompetitionEntry {
  id: string
  user_id: string
  category_id: string
  category: { id: string; name: string } | null
  confirmed_at: string | null
  confirmed_by: string | null
  session_id: string | null
  joined_at: string
  /**
   * Set when this competitor is a child's own login: the child it belongs to.
   * Absent from a server that predates child accounts.
   */
  child_id?: string | null
  profile: CompetitorProfile | null
  session: { id: string; name: string; starts_at: string; ends_at: string } | null
}

/** A time slot as staff read it: "Group A (09:00–12:00)". */
export function groupLabel(session: { name: string; starts_at: string; ends_at: string }): string {
  return `${session.name} (${session.starts_at.slice(11, 16)}–${session.ends_at.slice(11, 16)})`
}

/**
 * The categories a competitor may be moved between: their own and every other
 * one of the same gender. The Worker enforces the same rule; this only keeps
 * the picker from offering what would be refused.
 */
export function sameGenderCategories(
  entry: Pick<CompetitionEntry, 'category_id'>,
  categories: CompetitionCategory[],
): CompetitionCategory[] {
  const current = categories.find((c) => c.id === entry.category_id)
  if (!current) return []
  return categories.filter((c) => c.gender === current.gender)
}

/** Whether there is anywhere else to put this competitor at all. */
export function canMoveEntry(
  entry: Pick<CompetitionEntry, 'category_id' | 'session_id'>,
  categories: CompetitionCategory[],
  sessions: CompetitionSession[],
): boolean {
  return (
    sameGenderCategories(entry, categories).length > 1 ||
    sessions.some((s) => s.id !== entry.session_id)
  )
}

export interface CompetitionResult {
  id: string
  user_id: string
  route_id: string
  attempts: number | null
  logged_at: string
  /** null = the competitor logged it; set = gym staff entered or corrected it. */
  recorded_by: string | null
  profile: CompetitorProfile | null
  route: {
    id: string
    grade: number | null
    color: string | null
    section_id?: string | null
  } | null
}

/**
 * The discipline a route is graded and scored in. A single-discipline
 * competition answers for all its routes; in a combined one the route's own
 * section decides, by the same rule the standings RPC uses.
 */
export function routeDiscipline(
  competition: CompetitionDiscipline,
  sectionId: string | null | undefined,
  sections: { id: string; discipline: string | null }[],
): Discipline {
  if (competition !== 'combined') return competition
  return disciplineForSection(sections.find((s) => s.id === sectionId)?.discipline)
}

/**
 * Legal name for MemberIdentity, which renders the nickname itself. Falls back
 * to a neutral label so a row never renders blank.
 */
export function fullName(
  profile: { first_name: string | null; last_name: string | null } | null,
): string {
  if (!profile) return 'Climber'
  return [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Climber'
}
